import { useState, useMemo } from 'react';
import clsx from 'clsx';
import { Button, Input } from '@/components/ui';

interface SensitivityTestDef {
  id: string;
  name: string;
  inputNamedRange: string;
  outputNamedRanges: string[];
  method: 'absolute' | 'relative';
  defaultStepSize: number;
  steps: number;
}

interface SensitivityOverride {
  enabled?: boolean;
  displayNameOverride?: string;
  defaultStepSize?: number;
  steps?: number;
  method?: 'absolute' | 'relative';
}

interface InputDef {
  name: string;
  displayName: string;
  dataType: string;
}

interface OutputDef {
  name: string;
  displayName: string;
}

interface SensitivityTabProps {
  tests: SensitivityTestDef[];
  overrides: Record<string, SensitivityOverride>;
  inputs: InputDef[];
  outputs: OutputDef[];
  customTests: SensitivityTestDef[];
  onToggleEnabled: (testId: string, enabled: boolean) => void;
  onOverrideParam: (testId: string, field: string, value: unknown) => void;
  onAddCustomTest: (test: SensitivityTestDef) => void;
  onRemoveCustomTest: (testId: string) => void;
}

export function SensitivityTab({
  tests,
  overrides,
  inputs,
  outputs,
  customTests,
  onToggleEnabled,
  onOverrideParam,
  onAddCustomTest,
  onRemoveCustomTest,
}: SensitivityTabProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [showAddForm, setShowAddForm] = useState(false);

  const customTestIds = useMemo(() => new Set(customTests.map(t => t.id)), [customTests]);

  const toggle = (id: string) => setExpanded(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  if (tests.length === 0 && !showAddForm) {
    return (
      <div className="space-y-4">
        <p className="text-xs font-mono text-text-dim">No sensitivity tests configured for this model.</p>
        <Button variant="secondary" onClick={() => setShowAddForm(true)}>Add Test</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-xs font-mono text-text-dim">
        Enable/disable tests, override parameters, or add custom tests. Disabled tests won't appear in agent suggestions.
      </p>

      <div className="space-y-1">
        {tests.map(test => {
          const ov = overrides[test.id];
          const isEnabled = ov?.enabled !== false;
          const isCustom = customTestIds.has(test.id);
          const isExpanded = expanded.has(test.id);
          const displayName = ov?.displayNameOverride ?? test.name;

          return (
            <div
              key={test.id}
              className={clsx(
                'rounded-[2px] border overflow-hidden transition-opacity',
                isEnabled ? 'bg-input-bg border-panel-border' : 'bg-void border-panel-border opacity-50',
              )}
            >
              {/* Compact row */}
              <div className="flex items-center gap-3 px-3 py-2 cursor-pointer" onClick={() => toggle(test.id)}>
                <span className={clsx('text-micro font-mono transition-transform', isExpanded && 'rotate-90')}>▸</span>

                {/* Enable toggle */}
                <label className="flex items-center cursor-pointer" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={() => onToggleEnabled(test.id, !isEnabled)}
                    className="accent-[var(--color-accent)]"
                  />
                </label>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={clsx('text-sm font-mono truncate', isEnabled ? 'text-text-bright' : 'text-text-dim')}>
                      {displayName}
                    </span>
                    {isCustom && (
                      <span className="px-1.5 py-px rounded-[2px] text-micro font-mono font-semibold text-purple bg-purple/7 border border-purple/13">CUSTOM</span>
                    )}
                  </div>
                  <div className="text-micro font-mono text-text-dim">
                    {test.inputNamedRange} → {test.outputNamedRanges.length} output{test.outputNamedRanges.length !== 1 ? 's' : ''}
                    <span className="ml-2">·</span>
                    <span className="ml-2">{ov?.method ?? test.method}</span>
                    <span className="ml-2">·</span>
                    <span className="ml-2 tabular-nums">step {ov?.defaultStepSize ?? test.defaultStepSize}</span>
                    <span className="ml-2">·</span>
                    <span className="ml-2 tabular-nums">±{ov?.steps ?? test.steps}</span>
                  </div>
                </div>

                {isCustom && (
                  <button
                    onClick={e => { e.stopPropagation(); onRemoveCustomTest(test.id); }}
                    className="text-micro font-mono text-danger hover:text-danger/80 cursor-pointer bg-transparent border-none"
                    title="Remove custom test"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Expanded detail — parameter overrides */}
              {isExpanded && (
                <div className="px-3 pb-3 pt-1 border-t border-panel-border/50 space-y-3 ml-8" onClick={e => e.stopPropagation()}>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-micro font-mono text-text-dim mb-1">Step Size</label>
                      <Input
                        type="number"
                        value={ov?.defaultStepSize ?? test.defaultStepSize}
                        onChange={e => onOverrideParam(test.id, 'defaultStepSize', parseFloat(e.target.value) || test.defaultStepSize)}
                        className="text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-micro font-mono text-text-dim mb-1">Steps (±)</label>
                      <Input
                        type="number"
                        value={ov?.steps ?? test.steps}
                        onChange={e => onOverrideParam(test.id, 'steps', parseInt(e.target.value, 10) || test.steps)}
                        className="text-xs"
                      />
                    </div>
                    <div>
                      <label className="block text-micro font-mono text-text-dim mb-1">Method</label>
                      <select
                        value={ov?.method ?? test.method}
                        onChange={e => onOverrideParam(test.id, 'method', e.target.value)}
                        className="w-full bg-input-bg border border-panel-border rounded-[2px] px-2 py-1.5 text-xs font-mono text-text-bright focus:border-accent/30 focus:outline-none"
                      >
                        <option value="absolute">Absolute</option>
                        <option value="relative">Relative</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-micro font-mono text-text-dim mb-1">Display Name Override</label>
                    <Input
                      value={ov?.displayNameOverride ?? ''}
                      onChange={e => onOverrideParam(test.id, 'displayNameOverride', e.target.value || undefined)}
                      placeholder={test.name}
                      className="text-xs"
                    />
                  </div>
                  <div className="text-micro font-mono">
                    <span className="text-text-dim">Outputs: </span>
                    <span className="text-text-muted">{test.outputNamedRanges.join(', ')}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add custom test */}
      {showAddForm ? (
        <AddTestForm
          inputs={inputs}
          outputs={outputs}
          onAdd={(test) => { onAddCustomTest(test); setShowAddForm(false); }}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <Button variant="secondary" onClick={() => setShowAddForm(true)}>+ Add Custom Test</Button>
      )}
    </div>
  );
}

// ─── Add Test Form ───────────────────────────────────────────────

function AddTestForm({
  inputs,
  outputs,
  onAdd,
  onCancel,
}: {
  inputs: InputDef[];
  outputs: OutputDef[];
  onAdd: (test: SensitivityTestDef) => void;
  onCancel: () => void;
}) {
  const numericInputs = useMemo(
    () => inputs.filter(i => i.dataType !== 'string' && i.dataType !== 'date'),
    [inputs],
  );

  const [selectedInput, setSelectedInput] = useState(numericInputs[0]?.name ?? '');
  const [selectedOutputs, setSelectedOutputs] = useState<Set<string>>(new Set());
  const [method, setMethod] = useState<'absolute' | 'relative'>('relative');
  const [stepSize, setStepSize] = useState(0.05);
  const [steps, setSteps] = useState(2);

  const toggleOutput = (name: string) => setSelectedOutputs(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const handleSubmit = () => {
    if (!selectedInput || selectedOutputs.size === 0) return;
    onAdd({
      id: `custom_${selectedInput.toLowerCase()}_${Date.now()}`,
      name: `${inputs.find(i => i.name === selectedInput)?.displayName ?? selectedInput}`,
      inputNamedRange: selectedInput,
      outputNamedRanges: [...selectedOutputs],
      method,
      defaultStepSize: stepSize,
      steps,
    });
  };

  return (
    <div className="border border-accent/20 rounded-[2px] bg-input-bg p-4 space-y-3">
      <h4 className="text-xs font-mono font-semibold text-accent tracking-[0.8px]">ADD CUSTOM TEST</h4>

      <div>
        <label className="block text-micro font-mono text-text-dim mb-1">Input to Vary</label>
        <select
          value={selectedInput}
          onChange={e => setSelectedInput(e.target.value)}
          className="w-full bg-input-bg border border-panel-border rounded-[2px] px-2 py-1.5 text-xs font-mono text-text-bright focus:border-accent/30 focus:outline-none"
        >
          {numericInputs.map(i => (
            <option key={i.name} value={i.name}>{i.displayName} ({i.name})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-micro font-mono text-text-dim mb-1">Outputs to Track ({selectedOutputs.size} selected)</label>
        <div className="max-h-32 overflow-y-auto border border-panel-border rounded-[2px] p-2 space-y-0.5">
          {outputs.map(o => (
            <label key={o.name} className="flex items-center gap-2 cursor-pointer py-0.5">
              <input
                type="checkbox"
                checked={selectedOutputs.has(o.name)}
                onChange={() => toggleOutput(o.name)}
                className="accent-[var(--color-accent)]"
              />
              <span className="text-micro font-mono text-text-muted">{o.displayName}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-micro font-mono text-text-dim mb-1">Step Size</label>
          <Input type="number" value={stepSize} onChange={e => setStepSize(parseFloat(e.target.value) || 0.05)} className="text-xs" />
        </div>
        <div>
          <label className="block text-micro font-mono text-text-dim mb-1">Steps (±)</label>
          <Input type="number" value={steps} onChange={e => setSteps(parseInt(e.target.value, 10) || 2)} className="text-xs" />
        </div>
        <div>
          <label className="block text-micro font-mono text-text-dim mb-1">Method</label>
          <select
            value={method}
            onChange={e => setMethod(e.target.value as 'absolute' | 'relative')}
            className="w-full bg-input-bg border border-panel-border rounded-[2px] px-2 py-1.5 text-xs font-mono text-text-bright focus:border-accent/30 focus:outline-none"
          >
            <option value="absolute">Absolute</option>
            <option value="relative">Relative</option>
          </select>
        </div>
      </div>

      <div className="flex gap-2">
        <Button variant="primary" onClick={handleSubmit} disabled={!selectedInput || selectedOutputs.size === 0}>
          Add Test
        </Button>
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
